import { useIsMobile } from '@duke-hq/ui/components/Breakpoints/hooks';
import { useCallback, useMemo, useState } from 'react';
import type { ChatMeta, ChatUser } from '@duke-hq/svc-support-web';
import type { Drn } from '@duke-hq/drn/type';
import { ListDetail, type ListProps } from '@duke-hq/ui/components/ListDetail/ListDetail';

import { SupportContext } from './support/ctx';
import { ChatListItems } from './support/ChatList';
import { Detail } from './support/ChatDetail';
import { useChatActions } from './support/useChatActions';

import { t } from '@lingui/core/macro';
import { PlusIcon } from 'lucide-react';
import { Flex, Text, IconButton } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export function SupportCaseload({
  studentDrn,
  consultantDrn,
  providerDrn,
  selectedChat,
}: {
  studentDrn: Drn;
  providerDrn: Drn;
  consultantDrn?: Drn;
  selectedChat?: string;
}) {
  const isCondensed = useIsMobile();
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = trpcAdmin.support.chat.listCaseload.useInfiniteQuery(
    {
      studentDrn,
      providerDrn,
      filter: {
        open: true,
      },
    },
    {
      getNextPageParam: (lastPage) => lastPage?.cursor,
    },
  );

  const lookupUser = useCallback(
    (userDrn: Drn) => {
      if (data) {
        for (const page of data.pages) {
          if (!page) {
            continue;
          }

          if (page.users[userDrn]) {
            return page.users[userDrn] as ChatUser | undefined;
          }
        }
      }
    },
    [data],
  );

  const { chatActions, setChatUserCache } = useChatActions({
    lookupUser,
    studentDrn: studentDrn,
  });

  useMemo(() => {
    setChatUserCache((prev) => {
      const cache = { ...prev };
      if (!data) {
        return cache;
      }

      for (const page of data.pages) {
        if (!page) {
          continue;
        }

        for (const userDrn in page.users) {
          if (!cache[userDrn]) {
            cache[userDrn] = page.users[userDrn];
          }
        }
      }

      return cache;
    });
  }, [data, setChatUserCache]);

  const nextPage = useCallback(() => {
    // The next page has not yet been populated
    if (hasNextPage) {
      fetchNextPage();
    }
    if (!data) {
      return;
    }

    if (data?.pages.length > currentPage) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, hasNextPage, data, fetchNextPage]);

  const previousPage = useCallback(() => {
    if (currentPage === 0) {
      return;
    }
    setCurrentPage(currentPage - 1);
  }, [currentPage]);

  const { items, showNextPage, showPreviousPage } = useMemo(() => {
    let showNextPage,
      showPreviousPage = false;

    let items = new Map<string, ChatMeta>();

    if (!data || !data.pages) {
      return { items, showNextPage, showPreviousPage };
    }

    if (data.pages.length === 0) {
      return { items, showNextPage, showPreviousPage };
    }

    if (hasNextPage || currentPage < data.pages.length - 1) {
      showNextPage = true;
    }
    if (hasPreviousPage || currentPage > 0) {
      showPreviousPage = true;
    }

    const pageIndex = Math.min(currentPage, data.pages.length - 1);

    const page = data.pages[pageIndex];
    if (page?.chats) {
      const filteredItems = page.chats.filter(
        (item) => item.contextDrn && item.contextDrn.includes('student'),
      );

      items = new Map(filteredItems.map((item) => [item.id, item]));
    }

    return {
      items,
      showNextPage,
      showPreviousPage,
    };
  }, [hasNextPage, currentPage, data, hasPreviousPage]);

  const onSelect = useCallback(
    async (id: string) => {
      if (selectedChat) {
        await navigate(`../${id}`, { relative: 'path' });
      } else {
        await navigate(`./${id}`, { relative: 'path' });
      }
    },
    [navigate, selectedChat],
  );

  const createChat = trpcAdmin.support.createCaseloadSupportChatWithProviderUser.useMutation({
    onSuccess: async (data) => {
      if (data.id) {
        onSelect(data.id);
      }
    },
    onSettled: () => {
      utils.support.chat.listCaseload.invalidate();
    },
  });
  const utils = trpcAdmin.useUtils();

  const onCreateChat = useCallback(() => {
    if (!consultantDrn) {
      return;
    }
    createChat.mutate({
      studentDrn,
      consultantDrn,
      providerDrn,
    });
  }, [createChat, providerDrn, consultantDrn, studentDrn]);

  const List = useMemo(
    () =>
      function ChatList(props: ListProps<ChatMeta, string>) {
        return (
          <Flex direction="column" w="full" h="full">
            <Flex
              alignItems="center"
              alignSelf="stretch"
              justifyContent="space-between"
              p="16px 16px 8px 16px"
            >
              <Text textStyle="2xl" fontWeight="semibold" textTransform="capitalize">
                {t`Chats`}
              </Text>
              <IconButton
                rounded="full"
                size="sm"
                variant="outline"
                colorScheme="gray"
                aria-label={t`create chat`}
                icon={<PlusIcon size={20} />}
                onClick={onCreateChat}
              />
            </Flex>
            <ChatListItems {...props} />
          </Flex>
        );
      },
    [onCreateChat],
  );

  return (
    <SupportContext.Provider value={chatActions}>
      <ListDetail
        List={List}
        Detail={Detail}
        items={items}
        onSelect={onSelect}
        selectedId={selectedChat}
        isCondensed={isCondensed}
        isFetchingNextPage={isFetchingNextPage}
        isFetchingPreviousPage={isFetchingPreviousPage}
        hasNextPage={showNextPage}
        hasPreviousPage={showPreviousPage}
        isLoading={isLoading}
        isError={isError}
        nextPage={nextPage}
        previousPage={previousPage}
      />
    </SupportContext.Provider>
  );
}
